/**
 * フォーム編集画面用ページ監視カスタムフック
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';

import Log from '@/Logger';

import type { EWBCIHooks, PageSubscriptionEvent } from '@/feature/ewbci';
import { errmsg } from '../utils';

/** 設定オプション */
interface Options {
    hooks: EWBCIHooks;
    pageId: string;
    fieldCount: number;
}

/**
 * ページ監視の状態を管理するための列挙型
 */
export const FormSubscriptionStatus = {
    None: 'None', // 初期状態
    Initializing: 'Subscribing', // 初期化中
    Ready: 'Subscribed', // 準備完了・利用可能
    Unavailable: 'Unavailable', // 一時利用不可（自動回復）
    InitializeError: 'InitializeError', // 初期化中エラー（リトライ）
    Error: 'Error', // エラー状態（回復不可）
} as const;
export type FormSubscriptionStatus = (typeof FormSubscriptionStatus)[keyof typeof FormSubscriptionStatus];

/** フックの戻り値型 */
interface HookResult {
    status: FormSubscriptionStatus;
    isSyncing: boolean; // 同期中フラグ
    pageId: string;
    form: {
        /** フォームフィールドのキーと値のマップ */
        fields: Record<string, string>;
        /** 最終更新日時 */
        lastUpdatedAt: Date;
        /** 最終更新内容 */
        lastUpdatedFields: Record<string, string>;
    } | null; // null 初回データを受信していない状態
    initialForm?: {
        /** フォームフィールドのキーと値のマップ */
        form: Record<string, string>;
        /** 最終更新日時 */
        timestamp?: Date;
    }; // キャッシュがない場合はundefined
    pageImage: {
        /** ページ画像のBlob URL ※フック内でURL.revokeObjectURL()により解放される */
        blobUrl: string;
        /** 最終更新日時 */
        lastUpdatedAt: Date;
    } | null; // null 初回データを受信していない状態 
    clearCache: () => void; // キャッシュクリア関数
}

/** キャッシュされるデータの型 */
interface CachedPageData {
    form: {
        fields: Record<string, string>;
        lastUpdatedAt: Date;
        lastUpdatedFields: Record<string, string>;
    } | null;
    pageImage: {
        blobUrl: string;
        lastUpdatedAt: Date;
    } | null;
}

/** 状態 */
interface State {
    status: FormSubscriptionStatus,
    isSyncing: { // 同期中フラグ
        form: boolean;
        pageImage: boolean;
    };
    pageId: string,
    fieldCount: number, // フォームフィールド数
    form: {
        fields: Record<string, string>;
        lastUpdatedAt: Date;
        lastUpdatedFields: Record<string, string>;
    } | null;
    initialForm?: {
        form: Record<string, string>;
        timestamp?: Date;
    }; // キャッシュがない場合はundefined
    pageImage: {
        blobUrl: string;
        lastUpdatedAt: Date;
    } | null;
    error: Error | null;
    cache: Map<string, CachedPageData>; // pageId毎のキャッシュ
}

/** 初期状態 */
const initialState = (fieldCount: number): State => ({
    status: FormSubscriptionStatus.None,
    isSyncing: {
        form: false,
        pageImage: false,
    },
    pageId: '',
    fieldCount,
    form: null,
    pageImage: null,
    error: null,
    cache: new Map(),
});

/** アクション定義 */
type Action = {
    type: 'CHANGE_STATUS';
    pageId: string;
    status: FormSubscriptionStatus;
} | {
    type: 'CHANGE_PAGE_ID';
    pageId: string;
} | {
    type: 'UPDATE_FORM';
    form: Record<string, string>;
    timestamp: Date;
} | {
    type: 'UPDATE_PAGE_IMAGE';
    blobUrl: string; // Blob URL
    timestamp: Date;
} | {
    type: 'ERROR_ON_PAGE_IMAGE';
    error: unknown;
} | {
    type: 'ERROR_ON_SUBSCRIBE';
    error: unknown;
} | {
    type: 'ERROR';
    error: unknown;
} | {
    type: 'CACHE_CLEAR';
};

/** Reducer定義 */
const reducer = (state: State, action: Action) => {
    const logger = Log.getLogger();
    switch (action.type) {
        case 'CHANGE_STATUS':
            if (state.pageId !== action.pageId) return state;
            logger.ui.info("useFormSubscription.reducer", "ChangeStatus", `${state.status} -> ${action.status}`);
            let isSyncing = state.isSyncing;
            if (action.status === FormSubscriptionStatus.Initializing) { // 初期化の場合、同期中フラグを立てる
                isSyncing = { form: state.fieldCount > 0, pageImage: true };
            }
            return {
                ...state,
                isSyncing,
                status: action.status,
                error: null,
            };

        case 'CHANGE_PAGE_ID':
            logger.ui.info("useFormSubscription.reducer", "ChangePageID", `${state.pageId} -> ${action.pageId}`);

            // 現在のページデータをキャッシュに保存
            if (state.pageId && (state.form || state.pageImage)) {
                state.cache.set(state.pageId, {
                    form: state.form ? { fields: state.form.fields, lastUpdatedAt: state.form.lastUpdatedAt, lastUpdatedFields: {} } : null,
                    pageImage: state.pageImage,
                });
                logger.ui.info("useFormSubscription.reducer", "CachedPageData", `${state.pageId}`);
            }

            // 新しいページIDのキャッシュから復元
            const cachedData = state.cache.get(action.pageId);
            if (cachedData) {
                logger.ui.info("useFormSubscription.reducer", "RestoredFromCache", `${action.pageId}`);
                return {
                    ...state,
                    pageId: action.pageId,
                    form: cachedData.form,
                    initialForm: { form: cachedData.form?.fields ?? {}, timestamp: cachedData.form?.lastUpdatedAt },
                    error: null,
                };
            }

            // キャッシュがない場合は初期化
            return {
                ...state,
                pageId: action.pageId,
                form: null,
                initialForm: undefined,
                pageImage: null,
                error: null,
            };

        case 'UPDATE_FORM':
            logger.ui.info("useFormSubscription.reducer", "UpdateForm", `${Object.keys(action.form).length} fields, at ${action.timestamp}`);
            if (state.isSyncing.form === true && state.fieldCount !== Object.keys(action.form).length) {
                // フォームフィールド数が異なる場合はログ出力する（本来は通らない）
                logger.ui.error("useFormSubscription.reducer", "UpdateForm", `${Object.keys(action.form).length} fields updated, but ${state.pageId} has ${state.fieldCount} fields!`);
            }
            return {
                ...state,
                isSyncing: { ...state.isSyncing, form: false }, // form同期完了
                form: {
                    fields: {
                        ...(state.form?.fields ?? {}),
                        ...action.form
                    },
                    lastUpdatedAt: action.timestamp,
                    lastUpdatedFields: action.form,
                },
                error: null,
            };

        case 'UPDATE_PAGE_IMAGE':
            logger.ui.info("useFormSubscription.reducer", "UpdatePageImage", `${action.timestamp}`);
            // 以前のBlob URLを解放
            if (state.pageImage)
                URL.revokeObjectURL(state.pageImage.blobUrl);
            return {
                ...state,
                isSyncing: { ...state.isSyncing, pageImage: false }, // pageImage同期完了
                pageImage: {
                    blobUrl: action.blobUrl,
                    lastUpdatedAt: action.timestamp,
                },
                error: null,
            };

        case 'CACHE_CLEAR':
            logger.ui.info("useFormSubscription.reducer", "ClearCache");
            // 全てのキャッシュされたBlob URLを解放
            state.cache.forEach((cachedData, pageId) => {
                if (cachedData.pageImage?.blobUrl) {
                    URL.revokeObjectURL(cachedData.pageImage.blobUrl);
                    logger.ui.debug("useFormSubscription.reducer", "RevokedCachedBlobUrl", `${pageId}'s background`);
                }
            });
            // クリーンアップ
            return {
                ...state,
                cache: new Map(),
            };

        case 'ERROR_ON_SUBSCRIBE':
            logger.ui.error("useFormSubscription.reducer", "ErrorOnSubscribe", errmsg(action.error));
            return {
                ...state,
                status: FormSubscriptionStatus.InitializeError,
                error: action.error instanceof Error ? action.error : new Error(String(action.error)),
            };

        case 'ERROR_ON_PAGE_IMAGE':
            logger.ui.error("useFormSubscription.reducer", "ErrorOnPageImage", errmsg(action.error));
            return {
                ...state,
                status: FormSubscriptionStatus.Error,
                error: action.error instanceof Error ? action.error : new Error(String(action.error)),
                isSyncing: { ...state.isSyncing, pageImage: false }, // pageImage同期は一旦完了とする
            };

        case 'ERROR':
            logger.ui.error("useFormSubscription.reducer", "Error", errmsg(action.error));
            return {
                ...state,
                status: FormSubscriptionStatus.Error,
                error: action.error instanceof Error ? action.error : new Error(String(action.error)),
            };
        default:
            logger.ui.error("useFormSubscription.reducer", "UnknownAction", JSON.stringify(action));
            return state;
    }
};

/**
 * LantanaAPI接続状態の管理を担うカスタムフック
 * 
 * @param options 設定オプション
 * @returns LantanaAPI接続状態フックの戻り値
 */
export function useFormSubscription(options: Options): HookResult {
    const { hooks: { subscribePage, unsubscribePage }, pageId: target, fieldCount } = options;

    const cacheRef = useRef<Map<string, CachedPageData>>(new Map());
    // 状態
    const [state, dispatch] = useReducer((s: State, a: Action) => {
        const next = reducer(s, a);
        cacheRef.current = next.cache;
        return next;
    }, initialState(fieldCount));

    /** サブスクリプション状態変更イベントハンドラー */
    const handleSubscriptionEvent = useCallback((event: PageSubscriptionEvent) => {
        const logger = Log.getLogger();
        const { pageId } = event;
        if (pageId !== target) return; // 対象外のページIDなら無視
        switch (event.type) {
            case 'SUBSCRIBING':
                logger.ui.info('useFormSubscription', 'onChangeSubscriptionStatus', `Subscribing to page ${pageId}...`);
                dispatch({ type: 'CHANGE_STATUS', pageId, status: FormSubscriptionStatus.Initializing });
                break;

            case 'SUBSCRIBED':
                logger.ui.info('useFormSubscription', 'onChangeSubscriptionStatus', `Subscribed to page ${pageId}`);
                dispatch({ type: 'CHANGE_STATUS', pageId, status: FormSubscriptionStatus.Ready });
                break;

            case 'UNAVAILABLE':
                logger.ui.warn('useFormSubscription', 'onChangeSubscriptionStatus', `Page ${pageId} is temporarily unavailable`);
                dispatch({ type: 'CHANGE_STATUS', pageId, status: FormSubscriptionStatus.Unavailable });
                break;

            case 'UNSUBSCRIBED':
                logger.ui.info('useFormSubscription', 'onChangeSubscriptionStatus', `Unsubscribed from page ${pageId}`);
                dispatch({ type: 'CHANGE_STATUS', pageId, status: FormSubscriptionStatus.None });
                break;

            default:
                logger.ui.error('useFormSubscription', 'onChangeSubscriptionStatus', `Unknown subscription event: ${event}`);
                break;
        }
    }, [target]);

    /** フォーム変更イベントハンドラー */
    const onChangePageForm = useCallback((pageId: string, form: Record<string, string>, timestamp: Date) => {
        // const logger = Log.getLogger();
        if (pageId !== target) return; // 対象外のページIDなら無視
        // フォームを更新
        dispatch({ type: 'UPDATE_FORM', form, timestamp });
    }, [target]);

    /** ページ画像変更イベントハンドラー */
    const onChangePageImage = useCallback((pageId: string, pageImage: string, timestamp: Date) => {
        // const logger = Log.getLogger();
        if (pageId !== target) return; // 対象外のページIDなら無視
        try {
            // Blob URLを生成
            const binary = atob(pageImage);
            const array = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
            const blob = new Blob([array], { type: 'image/svg+xml' });
            const blobUrl = URL.createObjectURL(blob);
            // 画像URLを更新
            dispatch({ type: 'UPDATE_PAGE_IMAGE', blobUrl, timestamp });
        } catch (error: unknown) {
            dispatch({ type: 'ERROR_ON_PAGE_IMAGE', error });
        }
    }, [target]);

    useEffect(() => {
        if (target === '') return; // pageId未指定なら何もしない

        // 状態を初期化
        dispatch({ type: 'CHANGE_PAGE_ID', pageId: target });

        // サブスクリプション開始
        subscribePage(target, {
            handleSubscriptionEvent,
            onChangePageForm,
            onChangePageImage,
        }).catch((error) => {
            // subscribePageでエラー発生：リトライへ
            dispatch({ type: 'ERROR_ON_SUBSCRIBE', error });
            return;
        });

        // クリーンアップ処理
        return () => {
            unsubscribePage()
                .catch(() => { /* エラーログはunsubscribePage内で出力されるのでここでは無視 */ });
        };

    }, [target, subscribePage, unsubscribePage, handleSubscriptionEvent, onChangePageForm, onChangePageImage]);

    useEffect(() => {
        switch (state.status) { // 状態遷移フック
            case FormSubscriptionStatus.InitializeError: {
                const tid = setTimeout(() => {
                    // リトライ
                    subscribePage(target, {
                        handleSubscriptionEvent,
                        onChangePageForm,
                        onChangePageImage,
                    }).catch((error) => {
                        // subscribePageでエラー発生：リトライへ
                        dispatch({ type: 'ERROR_ON_SUBSCRIBE', error });
                        return;
                    });
                }, 3000); // 3秒後に再試行

                return () => clearTimeout(tid); // クリーンアップ
            }
            default:
                break; // それ以外の状態では何もしない
        }
    }, [state.status, state.error, subscribePage, target, handleSubscriptionEvent, onChangePageForm, onChangePageImage]);

    /** キャッシュクリア関数 */
    const clearCache = useCallback(() => {
        dispatch({ type: 'CACHE_CLEAR' });
    }, []);

    // コンポーネントアンマウント時のキャッシュクリーンアップ
    useEffect(() => {
        return () => {
            const logger = Log.getLogger();
            // アンマウント時にキャッシュされた全てのBlobURLを解放
            // ※reducerはアンマウント時にはdispatchを無視する
            logger.ui.info("useFormSubscription", "Cleanup");
            // 全てのキャッシュされたBlob URLを解放
            cacheRef.current.forEach((cachedData, pageId) => {
                if (cachedData.pageImage?.blobUrl) {
                    logger.ui.debug("useFormSubscription.reducer", "RevokedCachedBlobUrl", `${pageId}'s background`);
                    URL.revokeObjectURL(cachedData.pageImage.blobUrl);
                }
            });
        };
    }, []);

    return {
        status: state.status,
        isSyncing: state.isSyncing.form || state.isSyncing.pageImage,
        pageId: state.pageId,
        form: state.form,
        initialForm: state.initialForm,
        pageImage: state.pageImage,
        clearCache,
    };
}

export type FormSubscriptionOptions = Options;
export type FormSubscriptionHookResult = HookResult;

/* istanbul ignore next: MT用export指示のためカバレッジを取得しない */
export const __test__ = import.meta.env.NODE_ENV !== 'test' ? {} : { reducer };
